import { useEffect, useMemo, useState } from 'react';
import type { Branding } from '@helios/ui';
import clinicHeroImage from './clinic-hero.png';
import clinicLogoImage from './clinic-logo.png';
import intelisHeroImage from './hero.png';

type Props = {
  branding: Branding;
};

export function DrYoganandanLoginLeft({ branding }: Props) {
  const primaryColor = branding.theme?.primaryColor ?? '#2563EB';
  const azure = '#DBEAFE';
  const mint = '#D1FAE5';
  const ink = '#0F172A';
  const muted = '#475569';

  const [activeSlide, setActiveSlide] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const brandName = branding.orgName || 'Dr. S Yoganandan';

  const slides = useMemo(
    () => [
      {
        id: 'clinic',
        image: clinicHeroImage,
        eyebrow: 'Pediatric care you can trust',
        title: (
          <>
            Healthy children,
            <br />
            <span style={{ color: primaryColor }}>happy futures.</span>
          </>
        ),
        body: 'Compassionate pediatric care for every stage of your child’s growth and development.',
        details: [
          'Well Child Check-ups',
          'Immunization & Vaccines',
          'Treatment for Childhood Illnesses',
          'Growth & Development Monitoring',
        ],
        footer: 'Dedicated care for infants, children, and adolescents',
      },
      {
        id: 'intelis',
        image: intelisHeroImage,
        eyebrow: 'AI-powered healthcare intelligence',
        title: (
          <>
            Intelligence designed
            <br />
            for the <span style={{ color: primaryColor }}>future</span> of
            <br />
            healthcare.
          </>
        ),
        body: 'Connecting workflows, operations, patient journeys, and AI-driven insights across modern healthcare networks.',
        details: ['Connected Care', 'Smart Insights', 'Secure & Compliant'],
        footer: null,
      },
    ],
    [primaryColor],
  );

  const currentSlide = slides[activeSlide];

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(media.matches);

    updatePreference();
    media.addEventListener?.('change', updatePreference);

    return () => media.removeEventListener?.('change', updatePreference);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion || hasInteracted || slides.length <= 1) return;

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        setActiveSlide((current) => (current + 1) % slides.length);
      }
    }, 9000);

    return () => window.clearInterval(intervalId);
  }, [hasInteracted, prefersReducedMotion, slides.length]);

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        backgroundImage:
          `linear-gradient(90deg, rgba(239,246,255,0.98) 0%, rgba(239,246,255,0.92) 31%, rgba(219,234,254,0.34) 56%, rgba(219,234,254,0.00) 100%),` +
          `radial-gradient(980px 620px at 16% 12%, ${azure}F0 0%, ${azure}88 38%, ${azure}00 76%),` +
          `radial-gradient(920px 620px at 36% 88%, rgba(34,211,238,0.20) 0%, rgba(34,211,238,0.08) 36%, rgba(34,211,238,0.00) 72%),` +
          `linear-gradient(160deg, #EFF6FF 0%, #F8FBFF 42%, #EAF6FF 100%)`,
        backgroundRepeat: 'no-repeat, no-repeat, no-repeat, no-repeat',
        backgroundSize: '100% 100%, 100% 100%, 100% 100%, 100% 100%',
        backgroundPosition: 'center, center, center, center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      >
        <div
          key={currentSlide.id}
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 0,
            backgroundImage: `url('${currentSlide.image}')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center right',
            opacity: 1,
            filter: 'saturate(1.12) contrast(1.04)',
            transition: prefersReducedMotion ? 'none' : 'opacity 500ms ease',
            WebkitMaskImage:
              'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.52) 28%, rgba(0,0,0,0.95) 48%, rgba(0,0,0,1) 100%)',
            maskImage:
              'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.52) 28%, rgba(0,0,0,0.95) 48%, rgba(0,0,0,1) 100%)',
          }}
        />

        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
            backgroundImage:
              'linear-gradient(90deg, rgba(239,246,255,0.96) 0%, rgba(239,246,255,0.88) 32%, rgba(219,234,254,0.45) 52%, rgba(219,234,254,0.00) 74%)',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%',
            backgroundPosition: 'left center',
          }}
        />

        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
            backgroundImage:
              `linear-gradient(180deg, rgba(239,246,255,0.70) 0%, rgba(239,246,255,0.00) 22%, rgba(239,246,255,0.72) 100%),` +
              `linear-gradient(90deg, rgba(219,234,254,0.00) 0%, rgba(219,234,254,0.00) 70%, rgba(219,234,254,0.34) 100%)`,
            backgroundRepeat: 'no-repeat, no-repeat',
            backgroundSize: '100% 100%, 100% 100%',
            backgroundPosition: 'center, center',
          }}
        />

        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.28,
            filter: 'blur(70px)',
            backgroundColor: azure,
            width: '26rem',
            height: '26rem',
            top: '-8rem',
            left: '-8rem',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.18,
            filter: 'blur(70px)',
            backgroundColor: primaryColor,
            width: '20rem',
            height: '20rem',
            top: '18%',
            left: '18%',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.14,
            filter: 'blur(80px)',
            backgroundColor: mint,
            width: '600px',
            height: '600px',
            bottom: '-14rem',
            left: '16%',
          }}
        />
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 10,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '2rem',
            padding: '1.1rem 4rem 0.95rem',
            minHeight: '5.2rem',
          }}
        >
          <div
            style={{
              width: 'clamp(14rem, 22vw, 20rem)',
              height: '5.5rem',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              flexShrink: 1,
            }}
          >
            <img
              src={clinicLogoImage}
              alt="Dr. S Yoganandan Pediatric Clinic"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                transform: 'scale(1.2)',
                transformOrigin: 'center center',
              }}
            />
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              flexShrink: 0,
            }}
          >
            <div
              aria-hidden
              style={{
                width: '1px',
                height: '2.75rem',
                backgroundColor: 'rgba(148, 163, 184, 0.35)',
              }}
            />

            {branding.orgLogoHorizontalUrl ? (
              <img
                src={branding.orgLogoHorizontalUrl}
                alt={brandName}
                style={{
                  width: 'clamp(6.75rem, 8vw, 9rem)',
                  maxHeight: '2.5rem',
                  height: 'auto',
                  objectFit: 'contain',
                  objectPosition: 'right center',
                }}
              />
            ) : branding.orgLogoUrl ? (
              <img
                src={branding.orgLogoUrl}
                alt={brandName}
                style={{
                  width: 'clamp(2.25rem, 3vw, 3rem)',
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <span
                style={{
                  fontSize: '1rem',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: ink,
                }}
              >
                {brandName}
              </span>
            )}
          </div>
        </div>

        <div
          style={{
            padding: '1.25rem 4rem 3.25rem',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <div style={{ maxWidth: '44rem' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.55rem',
                alignSelf: 'flex-start',
                padding: '0.5rem 0.9rem',
                borderRadius: '9999px',
                backgroundColor: `${primaryColor}0F`,
                color: primaryColor,
                fontSize: '0.78rem',
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: '1.35rem',
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M12 3.5 13.35 8.25 18 9.6 13.35 10.95 12 15.5 10.65 10.95 6 9.6 10.65 8.25 12 3.5Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M18.5 14.5 19.2 17.05 21.75 17.75 19.2 18.45 18.5 21 17.8 18.45 15.25 17.75 17.8 17.05 18.5 14.5Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
              </svg>
              {currentSlide.eyebrow}
            </div>

            <h1
              style={{
                margin: 0,
                fontSize: 'clamp(2.2rem, 2.8vw, 3.55rem)',
                lineHeight: 1.12,
                letterSpacing: '-0.04em',
                color: ink,
                fontWeight: 760,
                maxWidth: '38rem',
              }}
            >
              {currentSlide.title}
            </h1>

            <p
              style={{
                margin: '1.35rem 0 0',
                fontSize: '1.05rem',
                lineHeight: 1.58,
                color: muted,
                maxWidth: '36rem',
              }}
            >
              {currentSlide.body}
            </p>

            <div
              aria-hidden
              style={{
                marginTop: '1.55rem',
                width: '3.9rem',
                height: '0.22rem',
                borderRadius: '9999px',
                backgroundColor: primaryColor,
              }}
            />

            <div
              style={{
                marginTop: '1.8rem',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.7rem',
                maxWidth: '39rem',
              }}
            >
              {currentSlide.details.map((detail) => (
                <div
                  key={detail}
                  style={{
                    padding: '0.72rem 0.9rem',
                    borderRadius: '0.9rem',
                    border: `1px solid ${primaryColor}1F`,
                    backgroundColor: 'rgba(255,255,255,0.58)',
                    color: ink,
                    fontSize: '0.88rem',
                    lineHeight: 1.35,
                    fontWeight: 700,
                    boxShadow: '0 14px 34px rgba(15,23,42,0.05)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  {detail}
                </div>
              ))}
            </div>

            {currentSlide.footer ? (
              <div
                style={{
                  marginTop: '1.35rem',
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                  color: muted,
                  fontWeight: 650,
                }}
              >
                {currentSlide.footer}
              </div>
            ) : null}

            <div
              role="tablist"
              aria-label="Login highlights"
              style={{
                marginTop: '1.8rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.55rem',
              }}
            >
              {slides.map((slide, index) => {
                const isActive = index === activeSlide;

                return (
                  <button
                    key={slide.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-label={`Show ${slide.eyebrow}`}
                    onClick={() => {
                      setActiveSlide(index);
                      setHasInteracted(true);
                    }}
                    style={{
                      width: isActive ? '2rem' : '0.62rem',
                      height: '0.62rem',
                      padding: 0,
                      border: 0,
                      borderRadius: '9999px',
                      backgroundColor: isActive ? primaryColor : `${primaryColor}45`,
                      cursor: 'pointer',
                      transition: prefersReducedMotion
                        ? 'none'
                        : 'width 220ms ease, background-color 220ms ease',
                    }}
                  />
                );
              })}
            </div>
          </div>

          <div
            style={{
              marginTop: 'auto',
              paddingTop: '2.5rem',
              fontSize: '0.875rem',
              color: '#94a3b8',
            }}
          >
            Powered by {branding.poweredBy ?? 'Intelis'}
          </div>
        </div>
      </div>
    </div>
  );
}
